<template>
  <button
    type="button"
    :id="id"
    :data-hit-id="hit.id"
    role="option"
    :aria-selected="active ? 'true' : 'false'"
    class="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left hover:bg-paper2"
    :class="active && 'bg-paper2'"
    @click="$emit('pick')"
  >
    <component :is="icon" class="w-4 h-4 shrink-0 text-forest" aria-hidden="true" />
    <span class="flex-1 min-w-0">
      <span class="flex items-center gap-1 text-[14px] font-semibold">
        <span class="truncate">{{ title }}</span>
        <VerifiedMark v-if="hit.kind === 'route'" :verified="hit.verified" :size="15" />
      </span>
      <span class="block text-[12px] text-ink2 truncate">{{ subtitle }}</span>
    </span>
    <span v-if="distance" class="shrink-0 text-right font-mono text-[12px] text-ink2">
      {{ formatDistance(distance.meters, units, locale) }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Route as RouteIcon } from "lucide-vue-next";
import VerifiedMark from "../ui/VerifiedMark.vue";
import { kindIcon, kindLabel } from "../../search/kinds";
import { pickLocalizedName } from "../../search/names";
import { formatDistance } from "../../search/geo";
import type { DistanceInfo } from "../../search/useSearch";
import type { SearchHit } from "../../search/types";

const props = defineProps<{ hit: SearchHit; distance: DistanceInfo | null; id?: string; active?: boolean }>();
defineEmits<{ (e: "pick"): void }>();

const { t, te, locale } = useI18n();
const units = computed(() => ({ km: t("units.km"), m: t("units.m") }));

const icon = computed(() => (props.hit.kind === "route" ? RouteIcon : kindIcon(props.hit.osmKind)));
// Точка - на языке интерфейса (names), маршрут - как назван.
const title = computed(() => {
  const h = props.hit;
  if (h.kind === "route") return h.name;
  return pickLocalizedName(h.names, h.name, locale.value) || kindLabel(h.osmKind, t, te);
});

const subtitle = computed(() => {
  const h = props.hit;
  if (h.kind !== "route") return [kindLabel(h.osmKind, t, te), h.ele != null ? t("search.elevation", { m: Math.round(h.ele) }) : ""].filter(Boolean).join(" · ");
  if (h.nearTo) return t("search.nearTo", { name: h.nearTo.name, distance: formatDistance(h.nearTo.distanceM, units.value, locale.value) });
  return `${h.distanceKm.toLocaleString(locale.value)} ${t("units.km")}`;
});
</script>
